import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { AppExperiment } from "@/features/viewport-lab/AppExperiment";
import { BenchExperiment } from "@/features/viewport-lab/BenchExperiment";
import { type ExperimentId, findExperiment } from "@/features/viewport-lab/experiments";
import { Gallery } from "@/features/viewport-lab/Gallery";
import { useWindowTakeover } from "@/features/viewport-lab/lab-parts";
import { RingExperiment } from "@/features/viewport-lab/RingExperiment";
import {
  defaultLabSettings,
  type LabSettings,
  parseLabSettings,
} from "@/features/viewport-lab/settings";

type LabSearch = LabSettings & { experiment?: ExperimentId };

export const Route = createFileRoute("/_authenticated/viewport-lab")({
  validateSearch: (search: Record<string, unknown>): LabSearch => ({
    ...parseLabSettings(search),
    experiment: findExperiment(search.experiment)?.id as ExperimentId | undefined,
  }),
  head: () => ({ meta: [{ title: "Viewport lab · Pax.co" }] }),
  component: ViewportLab,
});

/**
 * The gallery lives inside the app like any other page. An experiment takes
 * the whole window, because the app's frame is exactly what it is testing.
 */
function ViewportLab() {
  const { experiment: experimentId, ...settings } = Route.useSearch();
  const navigate = Route.useNavigate();
  const experiment = findExperiment(experimentId);

  if (!experiment) return <Gallery />;

  const props = {
    experiment,
    settings,
    onChange: (next: Partial<LabSettings>) =>
      void navigate({ search: current => ({ ...current, ...next }), replace: true }),
    onLeave: () => void navigate({ search: defaultLabSettings }),
  };

  return (
    <ExperimentWindow key={experiment.id} settings={settings}>
      {settings.scenario === "app" && <AppExperiment {...props} />}
      {settings.scenario === "ring" && <RingExperiment {...props} />}
      {settings.scenario === "bench" && <BenchExperiment {...props} />}
    </ExperimentWindow>
  );
}

function ExperimentWindow({ settings, children }: { settings: LabSettings; children: ReactNode }) {
  useWindowTakeover(settings);
  return createPortal(children, document.body);
}
